import { storableError } from '../../util/errors';
import { activateSubscriptionPlan, cancelSubscription, initiateRecurringPayment, requestSubscriptionPlan, retrieveSubscriptionId, retrieveUserSubscriptionInfo } from '../../util/api';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { isEmbeddedPaymentEnabled } from '../../util/stripeEmbedded';
import { SUBSCRIPTION_PAGE } from '../../util/urlHelpers';

// Action types
export const SUBSCRIBE_REQUEST = 'app/SubscriptionsPage/SUBSCRIBE_REQUEST';
export const SUBSCRIBE_SUCCESS = 'app/SubscriptionsPage/SUBSCRIBE_SUCCESS';
export const SUBSCRIBE_ERROR = 'app/SubscriptionsPage/SUBSCRIBE_ERROR';

export const ADD_SUBSCRIPTION_ID_REQUEST = 'app/SubscriptionsPage/ADD_SUBSCRIPTION_ID_REQUEST';
export const ADD_SUBSCRIPTION_ID_SUCCESS = 'app/SubscriptionsPage/ADD_SUBSCRIPTION_ID_SUCCESS';
export const ADD_SUBSCRIPTION_ID_ERROR = 'app/SubscriptionsPage/ADD_SUBSCRIPTION_ID_ERROR';

export const RETRIEVE_USER_SUBSCRIBE_REQUEST = 'app/SubscriptionsPage/RETRIEVE_USER_SUBSCRIBE_REQUEST';
export const RETRIEVE_USER_SUBSCRIBE_SUCCESS = 'app/SubscriptionsPage/RETRIEVE_USER_SUBSCRIBE_SUCCESS';
export const RETRIEVE_USER_SUBSCRIBE_ERROR = 'app/SubscriptionsPage/RETRIEVE_USER_SUBSCRIBE_ERROR';

export const CANCEL_SUBSCRIBE_REQUEST = 'app/SubscriptionsPage/CANCEL_SUBSCRIBE_REQUEST';
export const CANCEL_SUBSCRIBE_SUCCESS = 'app/SubscriptionsPage/CANCEL_SUBSCRIBE_SUCCESS';
export const CANCEL_SUBSCRIBE_ERROR = 'app/SubscriptionsPage/CANCEL_SUBSCRIBE_ERROR';

export const SET_SUBSCRIPTION_CLIENT_SECRET = 'app/SubscriptionsPage/SET_SUBSCRIPTION_CLIENT_SECRET';
export const CLEAR_SUBSCRIPTION_CLIENT_SECRET = 'app/SubscriptionsPage/CLEAR_SUBSCRIPTION_CLIENT_SECRET';

// Reducer
const initialState = {
    subscribeInProgress: false,
    subscribeError: null,
    addSubscriptionIdInProcess: false,
    addSubscriptionIdError: null,
    subscriptionInfo: null,
    retrieveUserSubscribeInProgress: false,
    retrieveUserSubscribeError: null,
    cancelSubscribeInProgress: false,
    cancelSubscribeError: null,
    subscriptionClientSecret: null,
    embeddedSubscriptionId: null,
};

export default function reducer(state = initialState, action = {}) {
    const { type, payload } = action;

    switch (type) {
        case SUBSCRIBE_REQUEST:
            return {
                ...state,
                subscribeInProgress: true,
                subscribeError: null,
            };

        case SUBSCRIBE_SUCCESS:
            return {
                ...state,
                subscribeInProgress: false,
                sessionURL: payload
            };

        case SUBSCRIBE_ERROR:
            return {
                ...state,
                subscribeInProgress: false,
                subscribeError: payload,
            };

        case ADD_SUBSCRIPTION_ID_REQUEST:
            return {
                ...state,
                addSubscriptionIdInProcess: true,
                addSubscriptionIdError: null,
            };

        case ADD_SUBSCRIPTION_ID_SUCCESS:
            return {
                ...state,
                addSubscriptionIdInProcess: false,
                addSubscriptionIdError: null
            };

        case ADD_SUBSCRIPTION_ID_ERROR:
            return {
                ...state,
                addSubscriptionIdInProcess: false,
                addSubscriptionIdError: payload,
            };

        case RETRIEVE_USER_SUBSCRIBE_REQUEST:
            return {
                ...state,
                retrieveUserSubscribeInProgress: true,
                retrieveUserSubscribeError: null,
            };

        case RETRIEVE_USER_SUBSCRIBE_SUCCESS:
            return {
                ...state,
                retrieveUserSubscribeInProgress: false,
                subscriptionInfo: payload
            };

        case RETRIEVE_USER_SUBSCRIBE_ERROR:
            return {
                ...state,
                retrieveUserSubscribeInProgress: false,
                retrieveUserSubscribeError: payload,
            };

        case CANCEL_SUBSCRIBE_REQUEST:
            return {
                ...state,
                cancelSubscribeInProgress: true,
                cancelSubscribeError: null,
            };

        case CANCEL_SUBSCRIBE_SUCCESS:
            return {
                ...state,
                cancelSubscribeInProgress: false,
            };

        case CANCEL_SUBSCRIBE_ERROR:
            return {
                ...state,
                cancelSubscribeInProgress: false,
                cancelSubscribeError: payload,
            };

        case SET_SUBSCRIPTION_CLIENT_SECRET:
            return {
                ...state,
                subscribeInProgress: false,
                subscriptionClientSecret: payload?.clientSecret || null,
                embeddedSubscriptionId: payload?.subscriptionId || null,
            };

        case CLEAR_SUBSCRIPTION_CLIENT_SECRET:
            return {
                ...state,
                subscriptionClientSecret: null,
                embeddedSubscriptionId: null,
            };
        default:
            return state;
    }
}

// Action creators
export const subscribeRequest = () => ({ type: SUBSCRIBE_REQUEST });
export const subscribeSuccess = (sessionURL) => ({
    type: SUBSCRIBE_SUCCESS,
    payload: sessionURL
});
export const subscribeError = e => ({
    type: SUBSCRIBE_ERROR,
    payload: e,
    error: true,
});

export const setSubscriptionClientSecret = ({ clientSecret, subscriptionId }) => ({
    type: SET_SUBSCRIPTION_CLIENT_SECRET,
    payload: { clientSecret, subscriptionId },
});
export const clearSubscriptionClientSecret = () => ({
    type: CLEAR_SUBSCRIPTION_CLIENT_SECRET,
});

export const addSubscriptionIdRequest = () => ({ type: ADD_SUBSCRIPTION_ID_REQUEST });
export const addSubscriptionIdSuccess = (sessionURL) => ({
    type: ADD_SUBSCRIPTION_ID_SUCCESS,
    payload: sessionURL
});
export const addSubscriptionIdError = e => ({
    type: ADD_SUBSCRIPTION_ID_ERROR,
    payload: e,
    error: true,
});

export const retrieveUserSubscribeRequest = () => ({ type: RETRIEVE_USER_SUBSCRIBE_REQUEST });
export const retrieveUserSubscribeSuccess = (data) => ({
    type: RETRIEVE_USER_SUBSCRIBE_SUCCESS,
    payload: data
});
export const retrieveUserSubscribeError = e => ({
    type: RETRIEVE_USER_SUBSCRIBE_ERROR,
    payload: e,
    error: true,
});

export const cancelSubscribeRequest = () => ({ type: CANCEL_SUBSCRIBE_REQUEST });
export const cancelSubscribeSuccess = () => ({
    type: CANCEL_SUBSCRIBE_SUCCESS,
});
export const cancelSubscribeError = e => ({
    type: CANCEL_SUBSCRIBE_ERROR,
    payload: e,
    error: true,
});

// Thunk
export const subscribeUser = (params) => async (dispatch, getState, sdk) => {
    dispatch(subscribeRequest());

    try {
        const state = getState();
        const currentUser = state.user.currentUser;
        const userId = currentUser?.id?.uuid;

        const subscriptionId = currentUser?.attributes?.profile?.metadata?.subscriptionId;

        const { tierName, paymentMethod } = params;

        const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
        const bodyParams = {
            subscriptionName: tierName,
            successURL: `${baseUrl}/${SUBSCRIPTION_PAGE}`,
            cancelURL: typeof window !== 'undefined' ? window.location.href : '',
        };

        /* The plan is granted by the server, never written from here. A free
         * plan cancels any card subscription on the way (server side). */
        if (tierName === 'basic'|| tierName === 'private') {
            await requestSubscriptionPlan({ tierName });
            dispatch(subscribeSuccess());
            await dispatch(fetchCurrentUser());
            return;
        }

        if (tierName === 'premium' && !subscriptionId && paymentMethod === 'stripe') {
            const embedded = isEmbeddedPaymentEnabled();
            const response = await initiateRecurringPayment({ ...bodyParams, embedded });
            const { success, sessionURL, clientSecret, subscriptionId: newSubscriptionId } = response;

            if (!success) {
                dispatch(subscribeError("Something went wrong during payment initiation"));
                return;
            }

            if (typeof window !== 'undefined') {
                localStorage.setItem("selectedTier", tierName);
            }

            if (embedded) {
                // Embedded: surface clientSecret + subscriptionId so the page can
                // open the modal and verify the subscription on success.
                dispatch(setSubscriptionClientSecret({ clientSecret, subscriptionId: newSubscriptionId }));
                return;
            }

            dispatch(subscribeSuccess(sessionURL));
            return;
        } else {
            console.log('paymentMethod', paymentMethod);
            /* Enterprise first, whatever the payment method: the server grants
             * it (business accounts, status 'pending' until confirmed) and
             * tells the admin inbox. Premium by invoice is temporary Premium. */
            if (tierName === 'enterprise' || paymentMethod === 'invoice') {
                await requestSubscriptionPlan({ tierName, paymentMethod });
                dispatch(subscribeSuccess());
                await dispatch(fetchCurrentUser());
                return;
            }
        }

        dispatch(subscribeSuccess());
        await dispatch(fetchCurrentUser());

    } catch (e) {
        console.error(e);
        dispatch(subscribeError(storableError(e)));
        throw e;
    }
};

// After a hosted Checkout: the server reads the plan from Stripe and grants it.
export const addSubscriptionIdToUserMetadata = ({ sessionId }) => async (dispatch, getState, sdk) => {
    dispatch(addSubscriptionIdRequest());

    try {
        await retrieveSubscriptionId({ sessionId });

        dispatch(addSubscriptionIdSuccess());
    } catch (e) {
        dispatch(addSubscriptionIdError(storableError(e)));
        throw e;
    }
};

// Embedded equivalent of addSubscriptionIdToUserMetadata: the subscription id
// is already known (returned by the initiate call); the server checks it with
// Stripe and grants the plan.
export const finalizeEmbeddedSubscription = ({ subscriptionId }) => async dispatch => {
    try {
        await activateSubscriptionPlan({ subscriptionId });

        await dispatch(fetchCurrentUser());
        return { success: true };
    } catch (e) {
        return { success: false, error: storableError(e) };
    }
};

export const fetchUserSubscriptionInfo = (params, search, config) => async (dispatch, getState, sdk) => {
    dispatch(retrieveUserSubscribeRequest());

    const state = getState();
    const currentUser = state.user.currentUser;
    const subscriptionId = currentUser?.attributes?.profile?.metadata?.subscriptionId;
    try {
        const response = await retrieveUserSubscriptionInfo({ subscriptionId });
        if (response.success) {
            dispatch(retrieveUserSubscribeSuccess(response.data));
            return response.data
        }
    } catch (error) {
        dispatch(retrieveUserSubscribeError(storableError(error)));
        throw error;
    }
};

// Thunk
export const cancelSubscribeUser = params => async (dispatch, getState, sdk) => {
    dispatch(cancelSubscribeRequest());

    try {
        const state = getState();
        const currentUser = state.user.currentUser;
        const userId = currentUser?.id?.uuid;

        const subscriptionId = currentUser?.attributes?.profile?.metadata?.subscriptionId;

        const response = await cancelSubscription({ subscriptionId, userId });
        if (response.success) {
            dispatch(cancelSubscribeSuccess());
            return { success: response.success };
        }
    } catch (e) {
        dispatch(cancelSubscribeError(storableError(e)));
        throw e;
    }
};

